import { PROPERTY_ICON } from "#app/lib/constants/property-icons";
import { z } from "zod";
import {
  Box,
  Button,
  Icon,
  Menu,
  MenuItem,
  MenuPopup,
  MenuTrigger,
  Popover,
  PopoverPopup,
  PopoverTrigger,
  Text,
  type IconName,
  type WidgetEmptyActionItem,
} from "@salestrak/ui";

import { ASSIGNEE_ICON } from "#app/lib/entity-visuals";
import { usePersistedState } from "#app/lib/hooks/use-persisted-state";
import { usePermission } from "#app/lib/hooks/use-permission";

/**
 * Shared config for a related-entity widget that renders BOTH a row-capped
 * snapshot on the Summary stack and a full dedicated tab — the same registered
 * widget, differing only by `config`:
 *
 * - Snapshot instance: `{ limit: 4, navigateTab: "projects" }` — caps rows and
 *   shows a "View all →" link that switches to the tab.
 * - Tab instance: `{}` — no limit, renders every row and scrolls internally.
 *
 * Loose, not strict: a widget's `validate` hook returns its parse result, and a
 * definition is free to add its own config keys on top of these two. Stripping
 * them would silently delete real config.
 */
export const relatedEntityConfigSchema = z.looseObject({
  limit: z.number().optional(),
  navigateTab: z.string().optional(),
});
export type RelatedEntityConfig = z.infer<typeof relatedEntityConfigSchema>;

/** The row cap for this instance, or undefined for the full (tab) view. */
export function relatedEntityLimit(config: RelatedEntityConfig): number | undefined {
  return typeof config.limit === "number" ? config.limit : undefined;
}

/**
 * The count + "View all →" that lives in a related-entity widget's title slot.
 * The count is always shown (mirroring the attachments count); the link only
 * appears in snapshot mode (`limit` set) when there are more rows than fit, and
 * navigates to the dedicated tab. Interactivity is safe inside the collapsible
 * stack header — `WidgetCard` stops the adornment's press from toggling collapse.
 */
export function RelatedEntityTitleAdornment({
  count,
  config,
  onNavigate,
}: {
  count: number;
  config: RelatedEntityConfig;
  onNavigate: (tab: string) => void;
}) {
  if (count === 0) return null;
  const limit = relatedEntityLimit(config);
  const navigateTab = typeof config.navigateTab === "string" ? config.navigateTab : undefined;
  const showViewAll = limit != null && count > limit && navigateTab != null;
  return (
    <Box direction="row" align="center" gap="xs">
      <Text size="xs" variant="muted" tabular>
        {count.toLocaleString()}
      </Text>
      {showViewAll ? (
        <Button variant="link" size="sm" onClick={() => onNavigate(navigateTab)}>
          <Text>View all</Text>
          <Icon name="arrow-right" size="xs" />
        </Button>
      ) : null}
    </Box>
  );
}

/* ---------------------------------------------------------------------------
 * Row display options — which qualifiers a related-entity row shows.
 *
 * A row's NAME is never an option: it is the row. Only the trailing/secondary
 * facts are, and only because they are the ones that stop being information
 * when they don't vary — six identical owner avatars are texture, not data, and
 * which ones those are depends on how a given company works. So the choice is
 * the reader's, kept client-local: this is a viewing preference, not workspace
 * data, and the widget view-state bag is the wrong home too (it is explicitly
 * transient and resets when the card unmounts).
 * ------------------------------------------------------------------------- */

export type RelatedEntityFields = Record<string, boolean>;

/**
 * One list's field set: where the prefs live, what shows by default, and the
 * rows the "..." popover offers.
 *
 * `storageKey` is per entity KIND, not per surface — a rep who hides Stage on
 * one Projects list means it on every other one too. The lists share a row
 * component; they should not disagree about what a Project row is.
 */
export type RelatedEntityFieldSpec<T extends RelatedEntityFields> = {
  storageKey: string;
  defaults: T;
  options: ReadonlyArray<{ key: keyof T & string; label: string; icon: IconName }>;
};

export type ProjectRowFields = {
  stage: boolean;
  assignees: boolean;
  age: boolean;
};

/**
 * Age stays off by default — the exception to the "hide what doesn't vary" rule
 * above, because here the problem is the opposite. The host already shows a
 * follow-up clock in the activity readout above this list, and that clock
 * measures the HOST's rhythm while a row's age measures THAT project's last
 * update. Two clocks on one screen that legitimately disagree read as a bug.
 * Age's job is queue triage ("what do I work next"), which belongs to the
 * Projects list, the board, and the Follow-ups queue where it drives the sort;
 * on a related-entity list the question is "what's here and where does it
 * stand", and the stage pie already answers it.
 */
export const PROJECT_ROW_FIELD_SPEC: RelatedEntityFieldSpec<ProjectRowFields> = {
  storageKey: "related-entity-fields:projects",
  defaults: { stage: true, assignees: true, age: false },
  options: [
    { key: "stage", label: "Stage", icon: PROPERTY_ICON.stage },
    { key: "assignees", label: "Assignees", icon: ASSIGNEE_ICON },
    { key: "age", label: "Follow up", icon: PROPERTY_ICON.followUp },
  ],
};

/**
 * Read a list's field prefs. The "..." popover writes them through the same
 * `usePersistedState` key, and that store is shared across hook instances — so
 * a toggle re-renders the header and every row at once.
 */
export function useRelatedEntityFields<T extends RelatedEntityFields>(
  spec: RelatedEntityFieldSpec<T>,
): T {
  return usePersistedState<T>(spec.storageKey, spec.defaults).state;
}

export type RelatedEntityAddDoors = {
  createLabel: string;
  addExistingLabel: string;
  onCreate: () => void;
  /** Only OPENS the picker — the caller mounts it controlled, because a menu
   *  item unmounts with its menu and cannot own what it launches. */
  onAddExisting: () => void;
};

/**
 * The two doors every related-entity list can grow through — create new, or
 * link something that already exists — as menu items. ONE list feeds both the
 * header's "+" (`RelatedEntityAddMenu`) and the empty section's action row
 * (`WidgetEmptyAction items`), so the two menus cannot drift apart.
 */
/**
 * Whether the viewer may grow a related-entity list at all. Both doors write:
 * creating makes a new Project, and linking an existing
 * one creates the relationship — the API checks `create.items` for either,
 * the key a Guest defaults to `no` on, whatever access level they hold on the
 * host entity. Every add door (header "+", empty-state rows, list-panel
 * toolbars) reads this one verdict so a Guest never meets a create door that
 * the server would refuse.
 */
export function useCanAddRelatedEntities(): boolean {
  return usePermission("create.items");
}

export function relatedEntityAddItems({
  createLabel,
  addExistingLabel,
  onCreate,
  onAddExisting,
}: RelatedEntityAddDoors): WidgetEmptyActionItem[] {
  return [
    { icon: "plus", label: createLabel, onClick: onCreate },
    { icon: "link", label: addExistingLabel, onClick: onAddExisting },
  ];
}

/**
 * The "+" a related-entity widget header mounts: a two-item menu naming both
 * doors — create new, or link something that already exists. The same pair the
 * empty section's action row opens and the custom-fields header offers from
 * its own "+", so a reader learns one grammar for "this list can grow".
 */
export function RelatedEntityAddMenu({
  ariaLabel,
  ...doors
}: RelatedEntityAddDoors & { ariaLabel: string }) {
  const canAdd = useCanAddRelatedEntities();
  if (!canAdd) return null;
  return (
    <Menu>
      <MenuTrigger
        render={<Button aria-label={ariaLabel} size="icon-sm" variant="ghost" />}
      >
        <Icon name="plus" size="md" />
      </MenuTrigger>
      <MenuPopup align="end">
        {relatedEntityAddItems(doors).map((item) => (
          <MenuItem key={item.label} onClick={item.onClick}>
            {item.icon ? <Icon name={item.icon} size="md" /> : null}
            <Text>{item.label}</Text>
          </MenuItem>
        ))}
      </MenuPopup>
    </Menu>
  );
}

/** One row: [16px leading slot] label … bare check when shown, and pressing the
 *  row toggles it. A check that is simply absent when off — the column-picker
 *  grammar, not a box — because the row's own label already names the thing
 *  being shown, so an empty box would just be a second place to read "off". */
function RelatedEntityFieldRow({
  icon,
  label,
  shown,
  onToggle,
}: {
  icon: IconName;
  label: string;
  shown: boolean;
  onToggle: (shown: boolean) => void;
}) {
  return (
    <Box
      direction="row"
      align="center"
      gap="sm"
      minHeight={32}
      paddingX="xs"
      rounded="sm"
      hoverBackground
      cursor="pointer"
      onClick={() => onToggle(!shown)}
      aria-label={`Toggle ${label}`}
    >
      <Box width={16} align="center" justify="center" shrink={false}>
        <Icon name={icon} size="md" color="muted" />
      </Box>
      <Box shrink fill="flex" minWidth={0}>
        <Text size="sm" truncate>
          {label}
        </Text>
      </Box>
      {shown ? <Icon name="check" size="sm" color="primary" /> : null}
    </Box>
  );
}

/**
 * The "..." a related-entity widget header mounts beside its "+".
 *
 * A Popover of check rows, NOT a Menu of checkbox items — the same call the
 * board and list Customize view panels make. A menu item dismisses the menu it
 * lives in, so three toggles cost three re-opens; a popover of rows takes all
 * three in one visit, which is how the reader actually uses it.
 */
export function RelatedEntityDisplayOptions<T extends RelatedEntityFields>({
  spec,
}: {
  spec: RelatedEntityFieldSpec<T>;
}) {
  const { state, update } = usePersistedState<T>(spec.storageKey, spec.defaults);
  return (
    <Popover>
      <PopoverTrigger
        render={<Button variant="ghost" size="icon-sm" aria-label="Display options" />}
      >
        <Icon name="ellipsis" size="md" />
      </PopoverTrigger>
      {/* `card` is the one-column-read-in-place rung, which is what a short
          stack of labelled check rows is; the rows own their inset, so the
          viewport spends none. */}
      <PopoverPopup align="end" width="card" padding="none">
        <Box paddingX="sm" paddingY="sm" gap="2xs">
          {/* Names the EFFECT, not the panel ("Display options" is already the
              trigger's label). Three checked rows with no header read as a
              filter — as if unchecking Follow up drops projects without one
              from the list. It only drops the qualifier from each row, and the
              header is what says so. Matches the list / board Customize view
              group-label treatment (xs, medium, faint). */}
          <Box paddingX="xs" paddingTop="2xs" paddingBottom="xs">
            <Text size="xs" weight="medium" variant="faint">
              Show on each row
            </Text>
          </Box>
          {spec.options.map((option) => (
            <RelatedEntityFieldRow
              key={option.key}
              icon={option.icon}
              label={option.label}
              // `defaults` carries every option key, so this read is total.
              // The fallback pays for the generic's index signature, which is
              // what the spec type is constrained by.
              shown={state[option.key] ?? false}
              onToggle={(shown) => update({ [option.key]: shown } as Partial<T>)}
            />
          ))}
        </Box>
      </PopoverPopup>
    </Popover>
  );
}
